import React, { useMemo } from 'react';
import { Address, BuildingWithConvertedAddresses } from '../../utils/types';
import { useSideAddresses } from '../../BuildingModal/BuildingView/useSideAddresses';
import { addressKey } from '../../HouseMap/sideAddresses';
import { formatStreetLine } from '../../utils/addressFormatting';
import FrontageRun from './FrontageRun';

interface BuildingFrontagesProps {
  building: BuildingWithConvertedAddresses;
  /** The address already shown as the main title — excluded from the list by
   * full address (street + number), so a same-street second entrance still
   * appears. */
  primaryAddress?: { street?: string; houseNumber?: string } | null;
  className?: string;
}

/**
 * The OTHER street frontages of a corner building (every detected frontage whose
 * full address differs from the primary). Footprint-detected via useSideAddresses
 * (cached per building, neighbour-filtered). Renders the shared FrontageRun (the
 * "⌐ frontage" row from Figma 2384:38727) in its --compact size for the on-map
 * preview card + left panel; no postcode here (those surfaces show the city in
 * their own header). Renders nothing for single-frontage buildings.
 */
const BuildingFrontages: React.FC<BuildingFrontagesProps> = ({
  building,
  primaryAddress,
  className,
}) => {
  const { addresses: sideAddresses, loading } = useSideAddresses(building);
  const primaryKey = addressKey(primaryAddress);

  const lines = useMemo(
    () =>
      sideAddresses
        .filter((a) => a.street && addressKey(a) !== primaryKey)
        .map((a: Address) => formatStreetLine(a)),
    [sideAddresses, primaryKey],
  );

  return (
    <FrontageRun
      lines={lines}
      loading={loading}
      className={`frontage-run--compact${className ? ` ${className}` : ''}`}
    />
  );
};

export default BuildingFrontages;
